// React
import React, { useState, useEffect, useRef, useContext } from 'react';

// Styles
import styles from './SalesChannelForm.module.scss';

// Material UI
import { Button, Grid, Typography, Tab } from '@material-ui/core';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';

// Third party
import { trackPromise } from 'react-promise-tracker';
import { Link as RouterLink, useHistory } from 'react-router-dom';

// Contexts
import { DetailContext } from '../../../contexts/detail';
import { SalesChannelContext } from '../../../contexts/saleschannel';

// Components
import FormMessage from '../FormMessage/FormMessage';

// Services
import {
  getDetailData as getGeneralData,
  saveData as saveGeneralData,
  getListingData as getAssociatedComponentsData,
  saveComponentsData
} from '../../../services/sales-channel';

// Shared
import eventBus from '../../../shared/eventBus';

// Hooks
import useMounted from '../../../hooks/useMounted';

// Tab data
import SalesChannelTabListing from './SalesChannelTabListing';
import { salesChannelExteriorColorsListing } from '../../../saleschannellistingdata/exteriorcolors';
import { salesChannelTrimsListing } from '../../../saleschannellistingdata/trims';
import { salesChannelUpholsteriesListing } from '../../../saleschannellistingdata/upholsteries';
import { salesChannelPackagesListing } from '../../../saleschannellistingdata/packages';
import { salesChannelOptionsListing } from '../../../saleschannellistingdata/options';
import { salesChannelAccessoriesListing } from '../../../saleschannellistingdata/accessories';

export default function SalesChannelForm() {
  // History hook
  const history = useHistory();

  // Detail Context
  const {
    typeOfData,
    title,
    itemId,
    isNewItem,
    endpoint,
    getInputs,
    baseUrl,
    getDetailId,
  } = useContext(DetailContext);

  // If the component is mounted
  const mounted = useMounted();

  // track if the form submit is success/fail
  const [messageState, setMessageState] = useState({
    open: false,
    state: '',
    message: '',
    messageActions: []
  });

  const trimListingFuncs = salesChannelTrimsListing();
  const colorListingFuncs = salesChannelExteriorColorsListing();
  const upholsteriesFuncs = salesChannelUpholsteriesListing();
  const packagesFuncs = salesChannelPackagesListing();
  const optionsFuncs = salesChannelOptionsListing();
  const accessoriesFuncs = salesChannelAccessoriesListing();

  // Listing functions per tab value
  const tabListingFuncs = {
    trims: trimListingFuncs,
    exteriorcolors: colorListingFuncs,
    upholsteries: upholsteriesFuncs,
    packages: packagesFuncs,
    options: optionsFuncs,
    accessories: accessoriesFuncs
  };
	
	// Step number
	const [stepNumber, setStepNumber] = useState(isNewItem ? 1 : 2);

  // Tab value
  const [tabValue, setTabValue] = useState('general');

  // Inputs
  const [inputs, setInputs] = useState(isNewItem ? getInputs() : []);

  // Current Sales Channel Info
  const [currentSalesChannelInfo, setCurrentSalesChannelInfo] = useState({});

  // Tab state
  const [tabModalSelectedValues, setTabModalSelectedValues] = useState([]); // selected rows in the "add new" modal grid (across pages)
  const [tabOwnSelectedValues, setTabOwnSelectedValues] = useState([]); // selected rows in an already connected to the current sales channel data grid (across pages)
  const [tabIsModified, setTabIsModified] = useState(false);
  const [tabPendingAdded, setTabPendingAdded] = useState([]); // rows to associate on "Save" click
  const [tabPendingRemoved, setTabPendingRemoved] = useState([]); // rows to disassociate on "Save" click
  const [tabSavedCount, setTabSavedCount] = useState(0); // incremented on each tab save so the tab grid reloads

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const getInputsRef = useRef(getInputs);

  // Component did mount (get data)
  useEffect(() => {
    // if we are loading an existing item
    // for the first time
    if (!isNewItem) {
      let promise = null;
      promise = getGeneralData({
        itemId,
        endpoint,
      });

      trackPromise(
        promise.then((newData) => {
          if (mounted.current && newData && newData.data) {
            setInputs(getInputsRef.current(newData.data, true));
            setCurrentSalesChannelInfo(newData.data);
          }
        })
      );
    }
  }, [typeOfData, itemId, endpoint, isNewItem, mounted]);
  
  // Track general tab field changes
  useEffect(() => {
    const maybeSetGeneralTabChangedState = (data_) => {
      let infoParam = data_.parameter;
      if (data_.parameter === 'salesChannelVehicleVersionId') {
        infoParam = 'vehicleDataVersionId';
      } else if (data_.parameter === 'salesChannelPricingVersionId') {
        infoParam = 'pricingDataVersionId';
      }
  
      if (currentSalesChannelInfo[infoParam] !== undefined && currentSalesChannelInfo[infoParam] !== data_.value) {
        setTabIsModified(true);
      }
    };

    eventBus.on('formInputChange_salesChannelDescription', maybeSetGeneralTabChangedState);
    eventBus.on('formInputChange_salesChannelCode', maybeSetGeneralTabChangedState);
    eventBus.on('formInputChange_salesChannelEnabled', maybeSetGeneralTabChangedState);
    eventBus.on('formInputChange_salesChannelVehicleVersionId', maybeSetGeneralTabChangedState);
    eventBus.on('formInputChange_salesChannelPricingVersionId', maybeSetGeneralTabChangedState);
  }, [currentSalesChannelInfo]);

  // Discard tab selections and pending changes
  const resetTabState = () => {
    setTabIsModified(false);
    setTabModalSelectedValues([]);
    setTabOwnSelectedValues([]);
    setTabPendingAdded([]);
    setTabPendingRemoved([]);
  };

  // Handle tab change.
  const handleTabChange = (event, newValue) => {
    if (tabValue === newValue) return;

    if (tabIsModified) {
      let confirmTabChange = window.confirm('There are unsaved changes on this page. If you choose to proceed, these changes will be lost.');

      if (confirmTabChange) {
        resetTabState();
        setTabValue(newValue);
      }
    } else {
      resetTabState();
      setTabValue(newValue);
    }
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    setMessageState({
      open: false,
      state: '',
      message: '',
      messageActions: []
    });

    // Next button or save on the general tab
    if (stepNumber === 1 || tabValue === 'general') {
      eventBus.dispatch('formInputMakeDisabled_countryId');

      const form = event.target;
      const formData = new FormData(form);

      let promise = saveGeneralData({
        formData,
        itemId,
        endpoint,
      });

      trackPromise(
        promise.then((response) => {
          let submitObj = {
            open: true
          };
  
          // Success
          if (response.succeeded === true) {
            submitObj['state'] = 'success';
  
            // New items (more than one) created
            if (isNewItem && Array.isArray(response.data)) {
              const counter = response.data.length;
  
              submitObj['messageText'] = `${counter} ${
                counter === 1 ? 'item' : 'items'
              } created!`;
            }
            // One item created or edited/saved
            else {
              submitObj['messageText'] = isNewItem ? 'Created!' : 'Saved!';
            }

            setTabIsModified(false);
            // Go to step 2
            setStepNumber(2);
          }
          // Error
          else {
            submitObj['state'] = 'error';
  
            submitObj['messageText'] = response.message
              ? response.message
              : 'There was a problem. Please try again!';
  
            submitObj['messageActions'] = [{ label: 'Ok', dismiss: true }];
          }
  
          // Show message
          setMessageState(submitObj);
  
          // if we have successfully created a new item
          // route the page url to the detail page url
          if (isNewItem && response.succeeded === true) {
            const detailId = getDetailId(response.data);
            history.push(`${baseUrl}/${detailId}`);
          }
        })
      );
    } else {
      // Step 2, Tab listing
      // The grid only holds the current page, so get every associated component and apply the pending changes to it
      const tabListing = tabListingFuncs[tabValue];
      const pendingRemovedIds = new Set(tabPendingRemoved.map(row => row.id));

      let promise = getAssociatedComponentsData({
        endpoint: tabListing.endpoint,
        getRows: tabListing.getRows,
        page: 0,
        sortModel: [tabListing.initialSort],
        activeFilters: {},
        isSalesChannelListing: true,
        salesChannelID: currentSalesChannelInfo.salesChannelId,
        isModal: false,
        fetchAll: true
      }).then((associatedData) => {
        // The BE replaces all associations with the ones sent, so never save from a partial list
        if (!Array.isArray(associatedData.data) || associatedData.totalRecords > associatedData.data.length) {
          throw new Error('Could not load all associated items. Please try again!');
        }

        const associatedComponents = new Set(
          associatedData.data.map(row => row.id).filter(id => !pendingRemovedIds.has(id))
        );
        tabPendingAdded.forEach(row => associatedComponents.add(row.id));

        return saveComponentsData({
          dataTypeIdentificator: tabValue,
          salesChannelId: currentSalesChannelInfo.salesChannelId,
          salesChannelVehicleVersionId: currentSalesChannelInfo.vehicleDataVersionId,
          salesChannelPricingVersionId: currentSalesChannelInfo.pricingDataVersionId,
          associatedComponents: Array.from(associatedComponents)
        });
      });

      trackPromise(
        promise.then((response) => {
          let submitObj = {
            open: true
          };
  
          // Success
          if (response.succeeded === true) {
            submitObj['state'] = 'success';
            submitObj['messageText'] = 'Saved!';
            resetTabState();
            setTabSavedCount(count => count + 1);
          }
          // Error
          else {
            submitObj['state'] = 'error';
            submitObj['messageText'] = response.message
              ? response.message
              : 'There was a problem. Please try again!';
            submitObj['messageActions'] = [{ label: 'Ok', dismiss: true }];
          }
  
          // Show message
          setMessageState(submitObj);
        }).catch((error) => {
          setMessageState({
            open: true,
            state: 'error',
            messageText: error.message,
            messageActions: [{ label: 'Ok', dismiss: true }]
          });
        })
      );
    }
  }

  // JSX

  return (
    <SalesChannelContext.Provider
      value={{
        currentSalesChannelInfo,
        tabModalSelectedValues,
        setTabModalSelectedValues,
        tabOwnSelectedValues,
        setTabOwnSelectedValues,
        setTabIsModified,
        tabPendingAdded,
        setTabPendingAdded,
        tabPendingRemoved,
        setTabPendingRemoved,
        tabSavedCount
      }}>
      <Grid item xs={12} md="auto">
        <Typography variant="h1" component="h1">
          {isNewItem ? 'New' : 'Edit'} {title}
        </Typography>
				<Typography variant="h2" component="h2" className={styles.subheader}>
					{isNewItem ? `Step ${stepNumber}: ${stepNumber === 1 ? `Create ${title}` : `Configuration Options | ${currentSalesChannelInfo.salesChannelDescription}` }` : currentSalesChannelInfo.salesChannelDescription}
				</Typography>
      </Grid>
      <form className={styles.form} onSubmit={handleFormSubmit}>
        <FormMessage
          messageOpen={messageState.open}
          messageState={messageState.state}
          messageText={messageState.messageText}
          messageActions={messageState.messageActions}></FormMessage>
        <div className={styles.tabs}>
          <TabContext value={tabValue}>
            <TabList onChange={handleTabChange} aria-label="Configuration Options" orientation="vertical" style={{display: stepNumber === 1 ? 'none' : 'flex'}}>
              <Tab label="General" value="general"/>
              <Tab label="Available Vehicles" value="trims"/>
              <Tab label="Exterior Colors" value="exteriorcolors"/>
              <Tab label="Upholstery" value="upholsteries"/>
              <Tab label="Packages" value="packages"/>
              <Tab label="Options" value="options"/>
              <Tab label="Accessories" value="accessories"/>
            </TabList>
            <TabPanel value="general" className={stepNumber === 1 ? 'step-1' : null}>
              {inputs &&
              inputs.map((row, i) => {
                return (
                  <div key={i} className={styles.row}>
                    <Grid container spacing={4}>
                      {row &&
                        row.map((input, j) => {
                          if (input.hidden) {
                            return <div key={j}>{input.component}</div>;
                          }
                          return (
                            <Grid
                              key={j}
                              item
                              xs={input.colSize.xs}
                              md={input.colSize.md}
                              lg={input.colSize.lg}
                              xl={input.colSize.xl}>
                              <Grid container spacing={1} alignItems="center">
                                <Grid
                                  item
                                  xs={input.size.xs}
                                  md={input.size.md}
                                  lg={input.size.lg}
                                  xl={input.size.xl}>
                                  {input.component}
                                </Grid>
                                {input.addNew}
                              </Grid>
                            </Grid>
                          );
                        })}
                    </Grid>
                  </div>
                );
              })}
            </TabPanel>
            <TabPanel value="trims">
              <SalesChannelTabListing
                dataTypeLabel="Vehicle"
                dataTypeIdentificator="trims"
                endpointListing={trimListingFuncs.endpoint} 
                endpointModal={trimListingFuncs.endpoint}
                initialSort={trimListingFuncs.initialSort}
                initialFilters={trimListingFuncs.initialFilters}
                getSalesChannelColumns={trimListingFuncs.getColumns}
                getSalesChannelRows={trimListingFuncs.getRows}
                getSalesChannelFilters={trimListingFuncs.getFilters}
              />
            </TabPanel>
            <TabPanel value="exteriorcolors">
              <SalesChannelTabListing
                dataTypeLabel="Color"
                dataTypeIdentificator="exteriorcolors"
                endpointListing={colorListingFuncs.endpoint} 
                endpointModal={colorListingFuncs.endpoint}
                initialSort={colorListingFuncs.initialSort}
                initialFilters={colorListingFuncs.initialFilters}
                getSalesChannelColumns={colorListingFuncs.getColumns}
                getSalesChannelRows={colorListingFuncs.getRows}
                getSalesChannelFilters={colorListingFuncs.getFilters}
              />
            </TabPanel>
            <TabPanel value="upholsteries">
              <SalesChannelTabListing
                dataTypeLabel="Upholstery"
                dataTypeIdentificator="upholsteries"
                endpointListing={upholsteriesFuncs.endpoint} 
                endpointModal={upholsteriesFuncs.endpoint} 
                initialSort={upholsteriesFuncs.initialSort}
                initialFilters={upholsteriesFuncs.initialFilters}
                getSalesChannelColumns={upholsteriesFuncs.getColumns}
                getSalesChannelRows={upholsteriesFuncs.getRows}
                getSalesChannelFilters={upholsteriesFuncs.getFilters}
              />
            </TabPanel>
            <TabPanel value="packages">
              <SalesChannelTabListing
                dataTypeLabel="Package"
                dataTypeIdentificator="packages"
                endpointListing={packagesFuncs.endpoint} 
                endpointModal={packagesFuncs.endpoint} 
                initialSort={packagesFuncs.initialSort}
                initialFilters={packagesFuncs.initialFilters}
                getSalesChannelColumns={packagesFuncs.getColumns}
                getSalesChannelRows={packagesFuncs.getRows}
                getSalesChannelFilters={packagesFuncs.getFilters}
              />
            </TabPanel>
            <TabPanel value="options">
              <SalesChannelTabListing
                dataTypeLabel="Options"
                dataTypeIdentificator="options"
                endpointListing={optionsFuncs.endpoint} 
                endpointModal={optionsFuncs.endpoint} 
                initialSort={optionsFuncs.initialSort}
                initialFilters={optionsFuncs.initialFilters}
                getSalesChannelColumns={optionsFuncs.getColumns}
                getSalesChannelRows={optionsFuncs.getRows}
                getSalesChannelFilters={optionsFuncs.getFilters}
              />
            </TabPanel>
            <TabPanel value="accessories">
              <SalesChannelTabListing
                dataTypeLabel="Accessory"
                dataTypeIdentificator="accessories"
                endpointListing={accessoriesFuncs.endpoint} 
                endpointModal={accessoriesFuncs.endpoint} 
                initialSort={accessoriesFuncs.initialSort}
                initialFilters={accessoriesFuncs.initialFilters}
                getSalesChannelColumns={accessoriesFuncs.getColumns}
                getSalesChannelRows={accessoriesFuncs.getRows}
                getSalesChannelFilters={accessoriesFuncs.getFilters}
              />
            </TabPanel>
          </TabContext>
        </div>
        <div className={styles.footer}>
          <Grid container spacing={3}>
            <Grid item xs={12} md="auto">
              <Button
                variant="outlined"
                disableElevation={true}
                color="secondary"
                size="large"
                component={RouterLink}
                to={baseUrl}>
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} md="auto">
              {stepNumber === 1 ? 
                <Button
									variant="contained"
									disableElevation={true}
									color="secondary"
									size="large"
									type="submit">
										Next
								</Button> : 
                <Button
									variant="contained"
									disableElevation={true}
									color="secondary"
									size="large"
									type="submit">
										Save
								</Button>
              }
            </Grid>
          </Grid>
        </div>
      </form>
    </SalesChannelContext.Provider>
  );
}
